import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Save, Loader2, ExternalLink } from "lucide-react";
import type { FieldErrors } from "react-hook-form";
import SaveSuccessIndicator from "./SaveSuccessIndicator";
import ErrorBanner from "./ErrorBanner";
import ValidationErrorBanner from "./ValidationErrorBanner";

interface EditPageHeaderProps {
  backUrl: string;
  isNew: boolean;
  newTitle: string;
  editTitle: string;
  entityName?: string;
  viewUrl?: string;
  formId: string;
  isSubmitting: boolean;
  isDirty: boolean;
  error?: Error | null;
  /**
   * `formState.errors` of the page's form. Pass it so a submit rejected by the Zod resolver
   * is visible even when the offending field has no inline error slot — otherwise nothing
   * happens on Save at all (see `ValidationErrorBanner`).
   */
  validationErrors?: FieldErrors;
  /**
   * `saveMutation.isSuccess` — required: every editor confirms a successful save with "Uloženo"
   * (vize F9.2), so a new header user cannot forget it.
   */
  isSuccess: boolean;
  extraActions?: React.ReactNode;
}

export default function EditPageHeader({
  backUrl,
  isNew,
  newTitle,
  editTitle,
  entityName,
  viewUrl,
  formId,
  isSubmitting,
  isDirty,
  error,
  validationErrors,
  isSuccess,
  extraActions,
}: EditPageHeaderProps) {
  const { t } = useTranslation();
  return (
    <>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to={backUrl} className="rounded-lg p-2 hover:bg-gray-100 dark:hover:bg-gray-700">
            <ArrowLeft className="h-5 w-5 text-gray-600 dark:text-gray-300" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              {isNew ? newTitle : editTitle}
            </h1>
            {entityName && (
              <p className="mt-1 text-gray-600 dark:text-gray-400">
                {isNew
                  ? t("shared.editPageHeader.createNew", { name: newTitle.toLowerCase() })
                  : t("shared.editPageHeader.editing", { name: entityName })}
              </p>
            )}
          </div>
        </div>

        <div className="flex items-center gap-3">
          {extraActions}
          <SaveSuccessIndicator isSuccess={isSuccess} />
          {!isNew && viewUrl && (
            <a href={viewUrl} target="_blank" rel="noopener noreferrer" className="btn-secondary">
              <ExternalLink className="h-5 w-5" />
              {t("shared.editPageHeader.view")}
            </a>
          )}
          <button
            type="submit"
            form={formId}
            disabled={isSubmitting || (!isDirty && !isNew)}
            className="btn-primary"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin" />
                {t("common.actions.saving")}
              </>
            ) : (
              <>
                <Save className="h-5 w-5" />
                {t("common.actions.save")}
              </>
            )}
          </button>
        </div>
      </div>

      <ErrorBanner error={error} />
      {validationErrors && <ValidationErrorBanner errors={validationErrors} />}
    </>
  );
}
